import _ from 'lodash';
import {
  ALLOWED_MODULE_HOST_SUFFIXES,
  EXTERNAL_MODULE_ROUTE_PREFIX,
  HOST_ID,
  HOST_MESSAGE_TYPES,
} from './constants';

/**
 * Key used in a module's route: its backend id.
 * @param {Object} module
 * @return {String}
 */
export const getModuleKey = module => String(module.id);

export const getModulePath = module =>
  `${EXTERNAL_MODULE_ROUTE_PREFIX}/${encodeURIComponent(getModuleKey(module))}`;

export const isExternalModulePath = path =>
  _.isString(path) && path.indexOf(`${EXTERNAL_MODULE_ROUTE_PREFIX}/`) === 0;

export const findModuleByKey = (modules, key) =>
  _.find(modules, module => getModuleKey(module) === key);

export const hasPlacement = (module, placement) => module.placement === placement;

const isAllowedHost = hostname => ALLOWED_MODULE_HOST_SUFFIXES.some(suffix =>
  hostname === suffix || hostname.endsWith(`.${suffix}`));

/**
 * Parses a module URL and returns its origin only when it is safe to embed: https on an
 * allowed domain, and never RC's own origin (the iframe sandbox would be meaningless there).
 * localhost is accepted outside production so a module can be developed locally.
 * @param {String} url
 * @return {String|null} The origin, or null when the URL must not be embedded
 */
export const getModuleOrigin = (url) => {
  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return null;
  }
  if (parsed.origin === window.location.origin) {
    return null;
  }
  const isLocalDev = process.env.NODE_ENV !== 'production' && parsed.hostname === 'localhost';
  if (isLocalDev) {
    return parsed.origin;
  }
  return parsed.protocol === 'https:' && isAllowedHost(parsed.hostname) ? parsed.origin : null;
};

/**
 * A child may only ask the host to open an RC page, never an external address.
 * @param {String} path
 * @return {Boolean}
 */
export const isInternalPath = path => _.isString(path) && /^\/(?![/\\])/.test(path);

/**
 * Keycloak subject of the logged-in user, read from the live access token.
 * @return {String|null}
 */
export const getKeycloakId = () => {
  const parsed = window.keycloakAuth && window.keycloakAuth.tokenParsed;
  return (parsed && parsed.sub) || null;
};

export const getLatestToken = jwtToken =>
  (window.keycloakAuth && window.keycloakAuth.token) || jwtToken || '';

/**
 * The user's organization nodes for a module: the response of the nodes API that the app
 * already loads at login (and again when the program is switched), narrowed to the three
 * fields that identify a node rather than handing over every field of the record.
 * @param {Array} nodeData the nodes API response
 * @return {Array} [{ id, nodeId, nodeName }]
 */
export const buildModuleNodes = nodeData =>
  _.map(_.isArray(nodeData) ? nodeData : [], node => ({
    id: _.get(node, 'id', null),
    nodeId: _.get(node, 'nodeId', null),
    nodeName: _.get(node, 'nodeName', null),
  }));

/**
 * Context handed to a module frame when it loads. The session cookie is deliberately not
 * included: it is HttpOnly and the token model is still being decided on DDM-1091.
 * @param {Object} params
 * @return {Object}
 */
export const buildModuleContext = ({
  userDetail, currentActiveProgram, jwtToken, nodeData,
}) => ({
  user: {
    keycloakId: getKeycloakId(),
    email: userDetail ? userDetail.username : null,
    firstName: userDetail ? userDetail.firstname : null,
    lastName: userDetail ? userDetail.lastname : null,
    fullName: userDetail ? userDetail.fullname : null,
    roles: _.map((currentActiveProgram && currentActiveProgram.roles) || [], 'name'),
    nodes: buildModuleNodes(nodeData),
  },
  program: {
    programId: currentActiveProgram ? currentActiveProgram.programId : null,
    platformProgramId: currentActiveProgram ? currentActiveProgram.platformProgramId : null,
    tenantId: currentActiveProgram ? currentActiveProgram.tenantId : null,
    name: currentActiveProgram ? currentActiveProgram.name : null,
    displayName: currentActiveProgram ? currentActiveProgram.displayName : null,
  },
  token: getLatestToken(jwtToken),
});

export const buildHostMessage = (type, payload) => ({ _parent: HOST_ID, type, ...payload });

export const buildContextMessage = context =>
  buildHostMessage(HOST_MESSAGE_TYPES.CONTEXT, { context });

export const buildTokenRefreshMessage = token =>
  buildHostMessage(HOST_MESSAGE_TYPES.TOKEN_REFRESH, { token });

/**
 * Reads the available-modules response. The list is empty when the program's External Modules
 * toggle is off, whatever else the response carries.
 * @param {Object} response { enabled, modules }
 * @return {Array}
 */
export const normalizeAvailableModules = (response) => {
  if (!response || response.enabled === false) {
    return [];
  }
  return _.isArray(response.modules) ? response.modules : [];
};
